import _ from "lodash";
import { getQueryUpdatedQueryString } from "Utils/NavigationHelpers";
import { WORK_BOOK_URL } from "Constants";
import {
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_DP,
} from "Constants/stringConstants";
import { getEnvironment } from "Utils/UrlHelpers";
import ACLStore from "lib/aclStore";

export const WORKBOOK_MODE_TYPES = {
  IMAGE_FEEDBACK: "image_feedback",
  ADD_OR_EDIT_STUDENT_TEMPLATE: "ADD_OR_EDIT_STUDENT_TEMPLATE",
  ADD_OR_EDIT_ATTACHMENT: "ADD_OR_EDIT_ATTACHMENT",
  WORKBOOK_MULTIPAGE_ADD_OR_EDIT: "WORKBOOK_MULTIPAGE_ADD_OR_EDIT",
  WORKBOOK_MULTIPAGE_VIEW: "WORKBOOK_VIEW",
  USE_TEMPLATE: "USE_TEMPLATE",
  WORKBOOK_FEEDBACK_VIEW: "WORKBOOK_FEEDBACK_VIEW",
  WORKBOOK_TEMPLATE_PREVIEW: "WORKBOOK_TEMPLATE_PREVIEW",
  WORKSHEET_TEACHER_CREATE: "WORKSHEET_TEACHER_CREATE",
  WORKSHEET_TEACHER_EVALUATE: "WORKSHEET_TEACHER_EVALUATE",
  WORKSHEET_STUDENT_EDIT: "WORKSHEET_STUDENT_EDIT",
  WORKSHEET_STUDENT_MOBILE_EDIT: "WORKSHEET_STUDENT_MOBILE_EDIT",
  WORKSHEET_STUDENT_SEB_EDIT: "WORKSHEET_STUDENT_SEB_EDIT",
  AI_TUTOR_V1: "AI_TUTOR_V1",
  AI_TUTOR_V2: "AI_TUTOR_V2",
  AI_TUTOR_VIEW_V2: "AI_TUTOR_VIEW_V2",
  AI_TUTOR_RESOURCE: "AI_TUTOR_RESOURCE",
};

export const WORKBOOK_IFRAME_ID_SELECTORS = {
  CLASSROOM: "workbook-iframe-classroom",
  DEFAULT: "workbook-iframe-global",
  THEATRE_MODE: "TheatreModeComponent",
  AI_TUTOR: "workbook-iframe-ai-tutor",
  AI_TUTOR_STUDENT_PREVIEW: "workbook-iframe-ai-tutor-student-preview",
};

export const shouldEnableWorkbookTemplateLibrary = ({
  curriculumProgramType,
}) => {
  switch (curriculumProgramType) {
    case CURRICULUM_TYPE_MYP:
    case CURRICULUM_TYPE_DP:
      return ACLStore.can("FeatureFlag:EnableWorkbookTemplateLibraryForMypDp");
    default:
      return true;
  }
};

export const shouldEnableWorkbookSchoolTemplateLibrary = ({
  curriculumProgramType,
}) => {
  switch (curriculumProgramType) {
    case CURRICULUM_TYPE_MYP:
    case CURRICULUM_TYPE_DP:
      return ACLStore.can(
        "FeatureFlag:EnableWorkbookSchoolTemplateLibraryForMypDp"
      );
    default:
      return ACLStore.can("FeatureFlag:EnableWorkbookSchoolTemplateLibrary");
  }
};

const adjustImageOrientation = imageOrientation => {
  let adjustedImageOrientation = imageOrientation;

  if (imageOrientation >= 360) {
    adjustedImageOrientation = imageOrientation % 360;
  } else if (imageOrientation < 0) {
    const fullRounds = Math.floor((imageOrientation * -1) / 360) + 1;
    adjustedImageOrientation = (fullRounds * 360 + imageOrientation) % 360;
  }

  return adjustedImageOrientation;
};

const isWorkbookViewMode = mode => {
  return _.includes(
    [
      WORKBOOK_MODE_TYPES["WORKBOOK_MULTIPAGE_VIEW"],
      WORKBOOK_MODE_TYPES["WORKBOOK_FEEDBACK_VIEW"],
    ],
    mode
  );
};

// returns the URL used to send to the workbook
export const getWorkBookUrl = ({
  attachmentId,
  imageOrientation = 0,
  mode = WORKBOOK_MODE_TYPES["IMAGE_FEEDBACK"],
  attachmentObj = {},
  disableArr = [],
  userInfo = {},
  enableArr = [],
  templateMetadata = {},
  showEditFrameOption = false,
}) => {
  //use community user info in community for viewing workbook
  const { token, org_id, id, user_type, email } =
    !_.isEmpty(userInfo) && isWorkbookViewMode(mode)
      ? userInfo
      : JSON.parse(_.get(localStorage, "userInfo", null)) || {};

  const userObj = {
    id,
    org_id,
    email,
    user_type,
  };

  const imageObj = {
    imageOrientation: adjustImageOrientation(imageOrientation),
  };

  const metadata = {
    ..._.get(attachmentObj, "metadata", {}),
  };

  let params = {
    token: token,
    mode: mode,
    user_metadata: JSON.stringify(userObj),
    img_metadata: JSON.stringify(imageObj),
    attachment_data: JSON.stringify(attachmentObj),
    disable_arr: JSON.stringify(disableArr),
    enable_arr: JSON.stringify(enableArr),
    metadata: JSON.stringify(metadata),
  };

  if (
    _.includes(
      [
        WORKBOOK_MODE_TYPES.AI_TUTOR_V1,
        WORKBOOK_MODE_TYPES.AI_TUTOR_V2,
        WORKBOOK_MODE_TYPES.AI_TUTOR_VIEW_V2,
        WORKBOOK_MODE_TYPES.AI_TUTOR_RESOURCE,
      ],
      mode
    )
  ) {
    const url =
      _.get(attachmentObj, "url") ||
      _.get(attachmentObj, "metadata.workbookUrl", "");
    if (url) {
      params.init_workbook_url = encodeURIComponent(url);
    }
  }

  if (attachmentId) {
    params = { ...params, id: attachmentId };
  }
  if (!_.isEmpty(templateMetadata)) {
    params["template_metadata"] = JSON.stringify(templateMetadata);
  }

  if (
    mode == WORKBOOK_MODE_TYPES.AI_TUTOR_V2 ||
    mode == WORKBOOK_MODE_TYPES.AI_TUTOR_VIEW_V2 ||
    mode == WORKBOOK_MODE_TYPES.AI_TUTOR_RESOURCE
  ) {
    // delete attachment_data,metadata, id  for AI_TUTOR_V2 mode to aviod reload
    delete params.attachment_data;
    delete params.metadata;
    delete params.id;
  }

  const paramString = getQueryUpdatedQueryString({
    url: WORK_BOOK_URL,
    params,
  });

  return `${WORK_BOOK_URL}?${paramString}`;
};

export const getWorkBookMessagePayload = ({
  attachmentId,
  imageOrientation = 0,
  mode = WORKBOOK_MODE_TYPES["IMAGE_FEEDBACK"],
  attachmentObj = {},
  disableArr = [],
  workbookUrl,
  userInfo = {},
  enableArr = [],
  questionDetails = {},
  isDrawingQuestion = false,
  isWorksheetWebView = false,
  shouldShowViewInstructions = false,
  templateMetadata = {},
  showEditFrameOption = false,
}) => {
  //use community user info in community for viewing workbook
  const { token, org_id, id, user_type, email } = !_.isEmpty(userInfo)
    ? userInfo
    : JSON.parse(_.get(localStorage, "userInfo", null)) || {};
  const locale = _.get(localStorage, "i18nextLng", "en");

  const userObj = {
    id,
    org_id,
    email,
    user_type,
  };

  const imageObj = {
    imageOrientation: adjustImageOrientation(imageOrientation),
  };

  let params = {
    token: token,
    mode: mode,
    user_metadata: JSON.stringify(userObj),
    img_metadata: JSON.stringify(imageObj),
    attachment_data: JSON.stringify(attachmentObj),
    disable_arr: JSON.stringify(disableArr),
    enable_arr: JSON.stringify(enableArr),
    locale,
    showEditFrameOption,
  };

  if (isDrawingQuestion && !_.isEmpty(questionDetails)) {
    params["question_details"] = JSON.stringify(questionDetails);
  }

  if (isDrawingQuestion) {
    params["is_drawing_question"] = true;
  }

  if (isWorksheetWebView) {
    params["is_worksheet_web_view"] = true;
  }

  if (shouldShowViewInstructions) {
    params["showViewInstructions"] = true;
  }

  if (showEditFrameOption) {
    params["showEditFrameOption"] = true;
  }

  if (attachmentId) {
    params = { ...params, id: attachmentId };
  }

  if (workbookUrl) {
    params = { ...params, workbookUrl: workbookUrl };
  }
  if (!_.isEmpty(templateMetadata)) {
    params["template_metadata"] = JSON.stringify(templateMetadata);
  }
  return params;
};

export const getWorkbookModeForMediaModal = ({
  attachmentType,
  workbookMode,
  mode,
}) => {
  if (attachmentType === "IMAGE") {
    if (workbookMode === "ADD_OR_EDIT_STUDENT_TEMPLATE") {
      return WORKBOOK_MODE_TYPES["ADD_OR_EDIT_STUDENT_TEMPLATE"];
    }
    return WORKBOOK_MODE_TYPES["USE_TEMPLATE"];
  } else if (workbookMode === WORKBOOK_MODE_TYPES["AI_TUTOR_RESOURCE"]) {
    return WORKBOOK_MODE_TYPES["AI_TUTOR_RESOURCE"];
  } else if (attachmentType === "WORKBOOK") {
    if (mode === "edit") {
      return WORKBOOK_MODE_TYPES["WORKBOOK_MULTIPAGE_ADD_OR_EDIT"];
    } else {
      return WORKBOOK_MODE_TYPES["WORKBOOK_MULTIPAGE_VIEW"];
    }
  } else {
    return workbookMode;
  }
};

export const getWorkbookOrigin = () => {
  const isStaging = _.includes(["staging", "development"], getEnvironment());
  return isStaging ? new URL(WORK_BOOK_URL).origin : "*";
};

export const isWorkbookParityEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableWorkbookParityUpgrades");
};
